/**
 * Type-Safe Icon Identifiers for Attack Graph
 *
 * This module provides compile-time type safety and runtime validation
 * for icon identifiers used in attack path nodes.
 *
 * @example
 * ```tsx
 * // Type-safe icon identifier
 * const icon: IconIdentifier = 'aws:Lambda';  // Valid
 * const icon: IconIdentifier = 'aws:Lamba';   // TypeScript error!
 *
 * // Runtime validation
 * const parsed = parseIconIdentifier('aws:Lambda');
 * if (parsed) {
 *   console.log(parsed.source, parsed.name); // 'aws', 'Lambda'
 * }
 * ```
 */

// ============================================================================
// Const Arrays - Source of Truth for Runtime Validation
// ============================================================================

export const AWS_ICONS = [
  'Lambda',
  'S3',
  'EC2',
  'ECS',
  'EKS',
  'RDS',
  'DynamoDB',
  'IAM',
  'Cognito',
  'ApiGateway',
  'CloudFormation',
  'SageMaker',
  'VPC',
  'CloudFront',
  'Route53',
  'SecretsManager',
  'KMS',
  'GuardDuty',
  'WAF',
  'Generic',
] as const;

export const AZURE_ICONS = [
  'Functions',
  'BlobStorage',
  'CosmosDB',
  'SQLDatabase',
  'AKS',
  'AppService',
  'VirtualMachine',
  'VirtualNetwork',
  'LoadBalancer',
  'EntraID',
  'KeyVault',
  'Sentinel',
  'Defender',
  'LogicApps',
  'APIManagement',
  'DevOps',
  'Repos',
  'Pipelines',
  'ADB2C',
  'Generic',
] as const;

export const GCP_ICONS = [
  'CloudFunctions',
  'CloudStorage',
  'CloudSQL',
  'BigQuery',
  'PubSub',
  'GKE',
  'ComputeEngine',
  'CloudRun',
  'IAM',
  'SecretManager',
  'VPC',
  'CloudArmor',
  'CloudBuild',
  'VertexAI',
  'Generic',
] as const;

export const AD_ICONS = [
  'DomainController',
  'Forest',
  'Domain',
  'OrganizationalUnit',
  'User',
  'Computer',
  'Group',
  'GroupPolicy',
  'CertificateAuthority',
  'ServiceAccount',
  'Trust',
  'Site',
] as const;

export const LUCIDE_ICONS = [
  'Computer',
  'Server',
  'Network',
  'Router',
  'Database',
  'Cloud',
  'Compute',
  'Serverless',
  'Storage',
  'Container',
  'User',
  'Group',
  'Principal',
  'CertAuthority',
  'WebApp',
  'Bot',
  'Firewall',
  'CrownJewel',
  // Social engineering badge icons
  'Mail',
  'MessageSquare',
  'Phone',
  'Shield',
  'Hash',
  'Briefcase',
  'Users',
  'MessageCircle',
  '', // default icon
] as const;

export const CICD_ICONS = [
  'Github',
  'GithubActions',
  'GithubCodespaces',
  'Gitlab',
  'Jenkins',
  'Circleci',
  'Travis',
  'AzureDevops',
  'Argocd',
  'Bamboo',
  'Terraform',
  'Pulumi',
  'Ansible',
  'Docker',
  'Kubernetes',
  'Vercel',
  'Netlify',
  'Heroku',
  'Cloudflare',
  'Bitbucket',
] as const;

export const WEB_ICONS = [
  'React',
  'Vue',
  'Angular',
  'Nextjs',
  'Nodejs',
  'Express',
  'Django',
  'Flask',
  'Nginx',
  'Apache',
  'Mongodb',
  'Postgresql',
  'Redis',
  'Javascript',
  'Typescript',
] as const;

export const IOT_ICONS = [
  'Actuator',
  'AMQP',
  'Antenna',
  'BLE',
  'Bluetooth',
  'Bootloader',
  'CANBus',
  'CTAG',
  'EEPROM',
  'Firmware',
  'Flash',
  'GPIO',
  'I2C',
  'JTAG',
  'Lora',
  'MCU',
  'MQTT',
  'NFC',
  'PCB',
  'RFID',
  'Sensor',
  'UART',
  'WiFi',
  'Zigbee',
  'ZWave',
] as const;

export const INFRASTRUCTURE_ICONS = [
  'Network',
  'Computer',
  'Server',
  'Firewall',
  'WebApplication',
  'Router',
  'Database',
] as const;

export const BRAND_ICONS = ['slack', 'linkedin', 'facebook', 'x'] as const;

export const SOCIAL_ICONS = [
  'UserEmail',
  'UserSMS',
  'UserPhone',
  'User2FA',
  'UserSlack',
  'UserLinkedIn',
  'UserFacebook',
  'UserTwitter',
] as const;

// ============================================================================
// Derived Types from Const Arrays
// ============================================================================

type AwsIconName = (typeof AWS_ICONS)[number];
type AzureIconName = (typeof AZURE_ICONS)[number];
type GcpIconName = (typeof GCP_ICONS)[number];
type AdIconName = (typeof AD_ICONS)[number];
type LucideIconName = (typeof LUCIDE_ICONS)[number];
type CicdIconName = (typeof CICD_ICONS)[number];
type WebIconName = (typeof WEB_ICONS)[number];
type IotIconName = (typeof IOT_ICONS)[number];
type InfrastructureIconName = (typeof INFRASTRUCTURE_ICONS)[number];
type BrandIconName = (typeof BRAND_ICONS)[number];
type SocialIconName = (typeof SOCIAL_ICONS)[number];

// ============================================================================
// Branded Identifier Types - Typos Caught at Compile Time!
// ============================================================================

export type AwsIcon = `aws:${AwsIconName}`;
export type AzureIcon = `azure:${AzureIconName}`;
export type GcpIcon = `gcp:${GcpIconName}`;
export type AdIcon = `ad:${AdIconName}`;
export type LucideIcon = `lucide:${LucideIconName}`;
export type CicdIcon = `cicd:${CicdIconName}`;
export type WebIcon = `web:${WebIconName}`;
export type IotIcon = `iot:${IotIconName}`;
export type InfrastructureIcon = `infrastructure:${InfrastructureIconName}`;
export type BrandIcon = `brand:${BrandIconName}`;
export type SocialIcon = `social:${SocialIconName}`;
export type CustomIcon = `custom:${string}`;
export type LegacyIcon = string; // For backwards compatibility with existing icons

// Union of all valid identifiers
export type IconIdentifier =
  | AwsIcon
  | AzureIcon
  | GcpIcon
  | AdIcon
  | LucideIcon
  | CicdIcon
  | WebIcon
  | IotIcon
  | InfrastructureIcon
  | BrandIcon
  | SocialIcon
  | CustomIcon
  | LegacyIcon;

// ============================================================================
// Icon Source Types
// ============================================================================

export type IconSource =
  | 'aws'
  | 'azure'
  | 'gcp'
  | 'ad'
  | 'lucide'
  | 'cicd'
  | 'web'
  | 'iot'
  | 'infrastructure'
  | 'brand'
  | 'social'
  | 'iconify'
  | 'devicon'
  | 'custom'
  | 'legacy';

// ============================================================================
// Runtime Validation
// ============================================================================

export interface ParsedIconIdentifier {
  source: IconSource;
  name: string;
}

/**
 * Parse and validate an icon identifier at runtime.
 *
 * Returns null for invalid identifiers, allowing graceful fallback.
 *
 * @param raw - Raw icon identifier string
 * @returns Parsed identifier with source and name, or null if invalid
 *
 * @example
 * ```tsx
 * const parsed = parseIconIdentifier('aws:Lambda');
 * // { source: 'aws', name: 'Lambda' }
 *
 * const invalid = parseIconIdentifier('aws:InvalidIcon');
 * // null
 *
 * const legacy = parseIconIdentifier('Server');
 * // { source: 'legacy', name: 'Server' }
 * ```
 */
export function parseIconIdentifier(raw: string): ParsedIconIdentifier | null {
  // Handle legacy icons (no prefix)
  if (!raw.includes(':')) {
    return { source: 'legacy', name: raw };
  }

  const [source, name] = raw.split(':') as [string, string];

  // Runtime validation against const arrays
  switch (source) {
    case 'aws':
      return (AWS_ICONS as readonly string[]).includes(name)
        ? { source: 'aws', name }
        : null;
    case 'azure':
      return (AZURE_ICONS as readonly string[]).includes(name)
        ? { source: 'azure', name }
        : null;
    case 'gcp':
      return (GCP_ICONS as readonly string[]).includes(name)
        ? { source: 'gcp', name }
        : null;
    case 'ad':
      return (AD_ICONS as readonly string[]).includes(name)
        ? { source: 'ad', name }
        : null;
    case 'lucide':
      return (LUCIDE_ICONS as readonly string[]).includes(name)
        ? { source: 'lucide', name }
        : null;
    case 'cicd':
      return (CICD_ICONS as readonly string[]).includes(name)
        ? { source: 'cicd', name }
        : null;
    case 'web':
      return (WEB_ICONS as readonly string[]).includes(name)
        ? { source: 'web', name }
        : null;
    case 'iot':
      return (IOT_ICONS as readonly string[]).includes(name)
        ? { source: 'iot', name }
        : null;
    case 'infrastructure':
      return (INFRASTRUCTURE_ICONS as readonly string[]).includes(name)
        ? { source: 'infrastructure', name }
        : null;
    case 'brand':
      return (BRAND_ICONS as readonly string[]).includes(name)
        ? { source: 'brand', name }
        : null;
    case 'social':
      return (SOCIAL_ICONS as readonly string[]).includes(name)
        ? { source: 'social', name }
        : null;
    case 'iconify':
      // Iconify icons don't have predefined list
      return { source: 'iconify', name };
    case 'custom':
      // Custom icons don't have predefined list
      return { source: 'custom', name };
    default:
      return null;
  }
}
